import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { vehicleService } from '../services/api';
import type { VehicleState, Vehicle, VehicleFilters, VehicleFormData, PaginatedResponse } from '../types';

// Initial state
const initialState: VehicleState = {
  vehicles: [],
  currentVehicle: null,
  loading: false,
  error: null,
  filters: {},
  pagination: {
    currentPage: 1,
    lastPage: 1,
    total: 0,
  },
};

// Async thunks for vehicles
export const fetchVehicles = createAsyncThunk(
  'vehicles/fetchVehicles',
  async (params: VehicleFilters & { page?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await vehicleService.getAll(params);
      return response.data as PaginatedResponse<Vehicle>;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch vehicles');
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as unknown as { vehicles: Pick<VehicleState, 'loading'> };
      return !state.vehicles.loading;
    },
  }
);

export const fetchVehicleById = createAsyncThunk(
  'vehicles/fetchVehicleById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await vehicleService.getById(id);
      return response.data.data as Vehicle;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch vehicle');
    }
  }
);

export const createVehicle = createAsyncThunk(
  'vehicles/createVehicle',
  async (vehicleData: VehicleFormData, { rejectWithValue }) => {
    try {
      const response = await vehicleService.create(vehicleData);
      return response.data.data as Vehicle;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create vehicle');
    }
  }
);

export const updateVehicle = createAsyncThunk(
  'vehicles/updateVehicle',
  async ({ id, data }: { id: number; data: Partial<VehicleFormData> }, { rejectWithValue }) => {
    try {
      const response = await vehicleService.update(id, data);
      return response.data.data as Vehicle;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update vehicle');
    }
  }
);

export const deleteVehicle = createAsyncThunk(
  'vehicles/deleteVehicle',
  async (id: number, { rejectWithValue }) => {
    try {
      await vehicleService.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete vehicle');
    }
  }
);

export const exportVehicles = createAsyncThunk(
  'vehicles/exportVehicles',
  async (params: VehicleFilters = {}, { rejectWithValue }) => {
    try {
      const response = await vehicleService.export(params);
      
      // Create blob URL for download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vehicles_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return 'Export successful';
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export vehicles');
    }
  }
);

export const uploadVehicleImages = createAsyncThunk(
  'vehicles/uploadImages',
  async ({ id, images }: { id: number; images: { primary_image?: File; additional_images?: File[] } }, { rejectWithValue }) => {
    try {
      const response = await vehicleService.uploadImages(id, images);
      return response.data.data as Vehicle;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload images');
    }
  }
);

export const removeVehicleImages = createAsyncThunk(
  'vehicles/removeImages',
  async ({ id, removeData }: { id: number; removeData: { remove_primary_image?: boolean; remove_additional_images?: string[] } }, { rejectWithValue }) => {
    try {
      const response = await vehicleService.removeImages(id, removeData);
      return { id, message: response.data.message };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove images');
    }
  }
);

// Vehicles slice
const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<VehicleFilters>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentVehicle: (state, action: PayloadAction<Vehicle | null>) => {
      state.currentVehicle = action.payload;
    },
    updateVehicleStatus: (state, action: PayloadAction<{ id: number; status: string }>) => {
      const vehicle = state.vehicles.find(v => v.id === action.payload.id);
      if (vehicle) {
        vehicle.status = action.payload.status as any;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch vehicles
    builder
      .addCase(fetchVehicles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.loading = false;
        state.vehicles = action.payload.data;
        state.pagination = {
          currentPage: action.payload.current_page,
          lastPage: action.payload.last_page,
          total: action.payload.total,
        };
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // Fetch vehicle by ID
    builder
      .addCase(fetchVehicleById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVehicleById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentVehicle = action.payload;
      })
      .addCase(fetchVehicleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // Create vehicle
    builder
      .addCase(createVehicle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVehicle.fulfilled, (state, action) => {
        state.loading = false;
        state.vehicles.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createVehicle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // Update vehicle
    builder
      .addCase(updateVehicle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVehicle.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.vehicles.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.vehicles[index] = action.payload;
        }
        if (state.currentVehicle?.id === action.payload.id) {
          state.currentVehicle = action.payload;
        }
      })
      .addCase(updateVehicle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // Delete vehicle
    builder
      .addCase(deleteVehicle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        state.loading = false;
        state.vehicles = state.vehicles.filter(v => v.id !== action.payload);
        if (state.currentVehicle?.id === action.payload) {
          state.currentVehicle = null;
        }
        state.pagination.total -= 1;
      })
      .addCase(deleteVehicle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // Export vehicles
    builder
      .addCase(exportVehicles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportVehicles.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportVehicles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // Upload vehicle images
    builder
      .addCase(uploadVehicleImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadVehicleImages.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.vehicles.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.vehicles[index] = action.payload;
        }
        if (state.currentVehicle?.id === action.payload.id) {
          state.currentVehicle = action.payload;
        }
      })
      .addCase(uploadVehicleImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

    // Remove vehicle images
    builder
      .addCase(removeVehicleImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeVehicleImages.fulfilled, (state, action) => {
        state.loading = false;
        // The API returns a message, not a vehicle object
        // We could optionally refetch the vehicle here if needed
      })
      .addCase(removeVehicleImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  setFilters, 
  clearFilters, 
  setCurrentVehicle, 
  updateVehicleStatus 
} = vehiclesSlice.actions;

export default vehiclesSlice.reducer; 